import { i18WithDefault as t, i18WithParams as tp } from '@shared/locale'
import { OrderClientDto, ReceiptDto } from 'service-api/src/types/api'

const VIRTUAL_CHECKIN_SETTING = { id: '', rate: 1, before: -1440 }

export type DepositPolicyType = 'seasonal' | 'default' | 'mixed'

export interface DepositPolicyInvoice {
  invoiceId: string
  serviceName: string
  startDate: string // locale-formatted date string e.g. "9/7/2026"
  depositLine: string // HTML string with <strong> tags
  refundLines: string[] // HTML strings with <strong> tags
}

export const getTitle = (type: DepositPolicyType) => {
  return type === 'seasonal' ? t('depositPolicy.title.seasonal') : t('depositPolicy.title.default')
}

export const getDescription = (type: DepositPolicyType) => {
  switch (type) {
    case 'seasonal':
      return t('depositPolicy.description.seasonal')
    case 'mixed':
      return t('depositPolicy.description.mixed')
    default:
      return null
  }
}

export const formatDepositLine = (rate: number, before: number): string => {
  const percent = Math.round(rate * 100)

  // before < 0 (check-in day or after)
  if (before < 0) {
    if (percent === 0) return t('depositPolicy.line.noDeposit')
    return tp('depositPolicy.line.due', { percent: String(percent) })
  }

  // before === 0 (before check-in day)
  if (before === 0) {
    if (percent === 0) return t('depositPolicy.line.noDepositBeforeCheckIn')
    return tp('depositPolicy.line.beforeCheckIn', { percent: String(percent) })
  }

  // before > 0 (days in advance)
  const days = Math.round(before / 1440)
  if (percent === 0) {
    return tp('depositPolicy.line.noDepositDaysInAdvance', { days: String(days) })
  }
  return tp('depositPolicy.line.daysInAdvance', { percent: String(percent), days: String(days) })
}

export interface PaymentPolicyEntry {
  paymentPolicy: { policyName: string; restrictionId: string } | null
  depositSettings: { id: string; rate: number; before: number }[]
  refundSettings: { id: string; rate: number; before: number }[]
  appliedDepositId: string
}

export const getDepositPolicyType = (
  paymentPolicies: Record<string, Pick<PaymentPolicyEntry, 'paymentPolicy'>>
): DepositPolicyType => {
  const allKeys = Object.keys(paymentPolicies)
  const hasDefault = allKeys.some((k) => paymentPolicies[k].paymentPolicy === null)
  const hasSeasonal = allKeys.some((k) => paymentPolicies[k].paymentPolicy !== null)
  if (hasSeasonal && hasDefault) return 'mixed'
  if (hasSeasonal) return 'seasonal'
  return 'default'
}

export const resolveDepositLine = (policyEntry: PaymentPolicyEntry): string => {
  const rawDepositSettings = policyEntry.depositSettings || []
  // Add virtual 100% same-day setting if no check-in day policy exists
  const hasCheckInEntry = rawDepositSettings.some((s) => s.before === VIRTUAL_CHECKIN_SETTING.before)
  const depositSettings = hasCheckInEntry ? rawDepositSettings : [...rawDepositSettings, { ...VIRTUAL_CHECKIN_SETTING }]
  const appliedSetting = depositSettings.find((s) => s.id === policyEntry.appliedDepositId)
  // Always matches: real ID matches a real setting, '' matches the virtual setting (id: '')
  return formatDepositLine(Number(appliedSetting!.rate), appliedSetting!.before)
}

export const resolvePolicyEntryForInvoice = (
  paymentPolicies: Record<string, PaymentPolicyEntry>,
  order: OrderClientDto,
  invoiceIds: string[] | undefined
) => {
  // Multi-period entries have one invoiceId; single-period entries list all.
  // First id is always the primary invoice for this entry.
  const invoiceId = invoiceIds?.[0] || ''
  const matchedInvoice = order.invoices?.find((inv) => inv.id === invoiceId)
  const invoiceData = matchedInvoice?.data as { paymentPolicyKey?: string } | undefined
  // Fallback to first policy for legacy orders without paymentPolicyKey.
  // paymentPolicies always has at least one entry (default) — orders without it don't reach this code.
  const policyKey = invoiceData?.paymentPolicyKey || Object.keys(paymentPolicies)[0]
  return { policyEntry: paymentPolicies[policyKey], matchedInvoice }
}

export const buildDepositInvoices = (order: OrderClientDto, receipt: ReceiptDto): DepositPolicyInvoice[] => {
  const paymentPolicies = (
    order.data as Record<string, never> & { paymentPolicies: Record<string, PaymentPolicyEntry> }
  ).paymentPolicies

  return (receipt.refundPolicyV2 || []).map((entry) => {
    const { policyEntry, matchedInvoice } = resolvePolicyEntryForInvoice(paymentPolicies, order, entry.invoiceIds)
    const depositLine = resolveDepositLine(policyEntry)

    const serviceName = matchedInvoice?.items?.[0]?.displayName || ''
    const period = matchedInvoice?.period?.[0]
    const startDate = period?.startDate
      ? new Date(period.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' })
      : ''

    return {
      invoiceId: entry.invoiceIds?.[0] || '',
      serviceName,
      startDate,
      depositLine,
      refundLines: (entry.policy || []).filter((line) => !line.startsWith('<h4')),
    }
  })
}
